import type { NavigationGuardWithThis } from 'vue-router'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import { useExperienceStore } from '@/features/experience/stores/experience.store'

/**
 * New-UI access gate (ENABLE_VUE3_NEW_UI). Runs after `authGuard`, so it only sees authenticated
 * navigations. Fail open: only a successful state load without ENABLE_VUE3_NEW_UI blocks access
 * (no staff bypass).
 *
 * While the new UI is still in private testing, a blocked user is redirected straight to the
 * equivalent classic (V2) page — NOT to the "private beta" screen, which isn't offered yet. The
 * BetaAccess route still exists for when the beta opens; it's just no longer the gate's target.
 */
export const experienceGuard: NavigationGuardWithThis<undefined> = async (to) => {
  if (to.meta.requiresAuth === false || to.name === 'BetaAccess') return true

  const auth = useAuthStore()
  if (!auth.isAuthenticated) return true // authGuard has already redirected to login

  const experience = useExperienceStore()
  await experience.ensureLoaded()
  if (experience.isBlocked) {
    // Full-page redirect to V2; cancel this SPA navigation so nothing renders in the meantime.
    await experience.redirectBlockedToClassic(to.fullPath)
    return false
  }

  experience.trackSessionStartOnce()
  return true
}
